/**
 * GenerateAllHelpButton — Bulk AI-generate help text for all controls in a form.
 *
 * Usage (sys-admin only):
 *   <GenerateAllHelpButton controls={[{ controlId, controlName }]} />
 *
 * Sends all field definitions to Gemini via POST /api/help/generate-all
 * Updates RC26MASTERDB + regenerates JSON cache.
 * Calls refreshDict() so tooltips update instantly.
 */
import React, { useState } from 'react';
import { useHelpContext } from '../../context/HelpContext';
import { Wand2, Loader, CheckCircle } from 'lucide-react';
import { API_BASE as API_URL } from '../../lib/api';


export function GenerateAllHelpButton({ controls = [], onDone }) {
  const { projectName, formName, canEdit, refreshDict } = useHelpContext();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(0);

  if (!canEdit || !controls.length || !formName) return null;

  const handleGenerate = async () => {
    setLoading(true);
    setDone(false);
    try {
      const res = await fetch(`${API_URL}/api/help/generate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project: projectName, form: formName, controls }),
      });
      const json = await res.json();
      if (json.success) {
        setCount(json.count || 0);
        setDone(true);
        await refreshDict();
        onDone?.();
        setTimeout(() => setDone(false), 4000);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  return (
    <button
      type="button"
      data-testid="generate-all-help-btn"
      onClick={handleGenerate}
      disabled={loading}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 6,
        background: done ? '#16a34a' : '#7c3aed',
        color: 'white', border: 'none', cursor: loading ? 'default' : 'pointer',
        fontSize: 11, fontWeight: 600, opacity: loading ? 0.7 : 1,
        transition: 'background 0.2s',
      }}
      title={`Generate AI help for all ${controls.length} fields in ${formName}`}
    >
      {loading ? <Loader size={12} style={{ animation: 'spin 0.8s linear infinite' }} />
        : done ? <CheckCircle size={12} /> : <Wand2 size={12} />}
      {loading ? 'Generating...' : done ? `${count} generated` : `AI Help All (${controls.length})`}
    </button>
  );
}
